class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  isHiddenCartLine(item) {
    if (!item) return true;
    if (window.PkHandling && PkHandling.matches(item)) return true;
    if (item.parent_relationship?.parent_key) return true;
    const type = String(item.product_type || '').trim().toLowerCase();
    return type === 'tax' || type === 'heading';
  }

  addedLineItem(parsedState) {
    const items = Array.isArray(parsedState?.items) ? parsedState.items : null;
    if (items && items.length) {
      return items.find((item) => !this.isHiddenCartLine(item)) || items[0];
    }
    if (parsedState?.key || parsedState?.product_title || parsedState?.title) return parsedState;
    return null;
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  productMarkup(item) {
    const title = item.product_title || item.title || '';
    const image = typeof item.image === 'string' ? item.image : item.featured_image?.url || '';
    const imageHtml = image
      ? `<div class="cart-notification-product__image global-media-settings"><img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(item.featured_image?.alt || title)}" width="70" height="70" loading="lazy"></div>`
      : '';
    const vendor =
      this.hasAttribute('data-show-vendor') && item.vendor
        ? `<p class="caption-with-letter-spacing light">${this.escapeHtml(item.vendor)}</p>`
        : '';
    const optionRows =
      !item.product_has_only_default_variant && Array.isArray(item.options_with_values)
        ? item.options_with_values
            .map(
              (option) =>
                `<div class="product-option"><dt>${this.escapeHtml(option.name)}:</dt><dd>${this.escapeHtml(option.value)}</dd></div>`
            )
            .join('')
        : '';
    const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
    const propertyRows = Object.entries(properties)
      .filter(([name, value]) => name && name.charAt(0) !== '_' && value != null && String(value) !== '')
      .map(([name, value]) => {
        const text = String(value);
        const body = text.includes('/uploads/')
          ? `<a href="${this.escapeHtml(text)}" class="link" target="_blank">${this.escapeHtml(text.split('/').pop())}</a>`
          : this.escapeHtml(text);
        return `<div class="product-option"><dt>${this.escapeHtml(name)}:</dt><dd>${body}</dd></div>`;
      })
      .join('');
    const options = optionRows || propertyRows ? `<dl>${optionRows}${propertyRows}</dl>` : '';
    const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
      ? `<p class="product-option">${this.escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>`
      : '';

    return `${imageHtml}<div>${vendor}<h3 class="cart-notification-product__name h4">${this.escapeHtml(title)}</h3>${options}${sellingPlan}</div>`;
  }

  bumpCartCount(quantity) {
    const add = Number(quantity) || 1;
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble span').forEach((el) => {
      const match = el.textContent.match(/\d+/);
      if (!match) return;
      el.textContent = el.textContent.replace(match[0], String(Number(match[0]) + add));
    });

    const button = document.getElementById('cart-notification-button');
    if (!button) return;
    const match = button.textContent.match(/\d+/);
    if (match) button.textContent = button.textContent.replace(match[0], String(Number(match[0]) + add));
  }

  refreshCartChrome() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const generation = (this.chromeGeneration || 0) + 1;
    this.chromeGeneration = generation;

    fetch(`${cartUrl}?sections=cart-icon-bubble,cart-notification-button`)
      .then((response) => response.json())
      .then((sections) => {
        if (generation !== this.chromeGeneration || !sections) return;
        ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
          const el = document.getElementById(id);
          if (!el || !sections[id]) return;
          el.innerHTML = this.getSectionInnerHTML(sections[id]);
        });
      })
      .catch(() => {});
  }

  renderContents(parsedState) {
    const sections = parsedState?.sections;
    const hasSectionHtml = !!(sections && (sections['cart-notification-product'] || sections['cart-icon-bubble']));
    const item = this.addedLineItem(parsedState);
    this.cartItemKey = item?.key || parsedState?.key;

    if (hasSectionHtml) {
      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el) return;

        el.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      });
    } else {
      const productEl = document.getElementById('cart-notification-product');
      if (productEl && item) productEl.innerHTML = this.productMarkup(item);
      if (item) this.bumpCartCount(item.quantity);
      this.refreshCartChrome();
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Try the explicit selector first (e.g. the keyed cart-notification-product)
    if (selector) {
      const targeted = doc.querySelector(selector);
      if (targeted) return targeted.innerHTML;
    }

    // Fallback to the section root or document body so we never throw
    const sectionRoot = doc.querySelector('.shopify-section') || doc.body;
    return sectionRoot ? sectionRoot.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
